import React from 'react';

import { cn } from '@/utils';

import type { Agent, CaseItem } from '../data/types';
import { AggregateCell } from './AggregateCell';
import { CaseCard } from './CaseCard';

const AGGREGATE_THRESHOLD = 3;

interface GridCellProps {
  agent: Agent;
  columnId: string;
  columnLabel: string;
  cases: CaseItem[];
  allAgents: Agent[];
  currentUserAgent: Agent | null;
  teamNameById: Map<string, string>;
  /** Per-card reassign — invalidates on settle. */
  onReassign: (caseId: string, newAgentId: string) => Promise<void>;
  /** Bulk reassign — coalesces invalidation, used by aggregate tiles. */
  onBulkReassign: (cases: CaseItem[], newAgentId: string) => Promise<{ failed: number }>;
  bulkInFlight: boolean;
  isReassigning: boolean;
  isFirstRow: boolean;
}

/** Split a bucket of cases by team while preserving first-seen order. Used to
 *  produce one aggregate card per team when a cell aggregates and its cases
 *  span more than one selected team. */
function partitionByTeam(cases: CaseItem[]): { teamId: string; cases: CaseItem[] }[] {
  const order: string[] = [];
  const groups = new Map<string, CaseItem[]>();
  for (const c of cases) {
    const tid = c.teamId || '';
    const arr = groups.get(tid);
    if (arr) arr.push(c);
    else {
      groups.set(tid, [c]);
      order.push(tid);
    }
  }
  return order.map(teamId => ({ teamId, cases: groups.get(teamId) ?? [] }));
}

/**
 * One body cell of the workforce grid. Picks between per-case cards
 * (count ≤ threshold) and per-team aggregate tiles (count > threshold).
 * When aggregating across multiple teams, renders one aggregate tile per
 * team partition.
 */
export const GridCell = React.memo(function GridCell({
  agent,
  columnId,
  columnLabel,
  cases,
  allAgents,
  currentUserAgent,
  teamNameById,
  onReassign,
  onBulkReassign,
  bulkInFlight,
  isReassigning,
  isFirstRow,
}: GridCellProps) {
  const shouldAggregate = cases.length > AGGREGATE_THRESHOLD;
  const teamPartitions = shouldAggregate ? partitionByTeam(cases) : [];
  const multipleTeams = teamPartitions.length > 1;

  return (
    <div
      className={cn(
        'border-r border-border last:border-r-0 p-2 space-y-2 align-top bg-background',
        !isFirstRow && 'border-t',
      )}
    >
      {shouldAggregate
        ? teamPartitions.map(({ teamId, cases: teamCases }) => (
            <AggregateCell
              key={`${columnId}::${teamId}`}
              agent={agent}
              columnLabel={columnLabel}
              teamLabel={multipleTeams ? (teamNameById.get(teamId) ?? '') : undefined}
              cases={teamCases}
              allAgents={allAgents}
              currentUserAgent={currentUserAgent}
              onBulkReassign={onBulkReassign}
              bulkInFlight={bulkInFlight}
              isReassigning={isReassigning}
            />
          ))
        : cases.map(c => (
            <CaseCard
              key={c.id}
              caseItem={c}
              agents={allAgents}
              currentUserAgent={currentUserAgent}
              onReassign={onReassign}
              isReassigning={isReassigning}
            />
          ))}
    </div>
  );
});
